import React from "react"
import autobind from "class-autobind"
import { IJobDocument, IDate, ISelectedFilterTemplateId,IUserFilterDocument, IPayrollPayCalendar, IUserFilter } from "../../../models"
import strings from "../../../localization/strings"
import moment from "moment"
import JobTimecards from "./JobTimecards"
import { Toggle } from "material-ui"
import JobDetailConsolidatedTimecards from "./JobDetailConsolidatedTimecards"
import AccountStore from "../../../stores/AccountStore"
import * as utils from "../../../helpers/utils"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as payrollHelper from "../../../helpers/payrollHelper"
import UserFilter from "../../../components/filter_template/employee/UserFilter"
import * as api from "../../../api"
import { EDefaultFilterValues } from "../../../models"

interface IProps {
    job: IJobDocument
}
interface IState {
    referenceDate: IDate
    selectedPayrollPayCalendar: IPayrollPayCalendar
    showConsolidatedTimecardView: boolean
    filterTemplates: IUserFilter[]
    selectedFilterTemplateId: ISelectedFilterTemplateId
    userFilter: IUserFilter
    lastFilterUpdate?: number
}

export default class JobDetailTimecards extends React.Component<IProps, IState> {
    private _isMounted: boolean = false;
    private referenceDateLocalStorageKey = "JobTimecardRefDate"
    private showConsolidatedTimecardViewLocalStorageKey = "JobTimecardConsolidatedView"
    
    constructor(props: IProps) {
        super(props)
        const storedJobTimecardRefDate = utils.getCookie(this.referenceDateLocalStorageKey)
        const storedShowConsolidatedTimecardView = utils.getCookie(this.showConsolidatedTimecardViewLocalStorageKey)

        this.state = {
            referenceDate: storedJobTimecardRefDate ? storedJobTimecardRefDate : moment(),
            selectedPayrollPayCalendar: payrollHelper.getSelectedPayrollPayCalendar(),
            showConsolidatedTimecardView: AccountStore.getPayClassEnabled() &&
                permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) &&
                storedShowConsolidatedTimecardView === "true",
            filterTemplates: [],
            selectedFilterTemplateId: null,
            userFilter: null
        }
        autobind(this)
    }
    async componentDidMount() {
        await this.loadFilterTemplates()      
    }

    handleSelectFilterTemplate(selectedFilterTemplateId: ISelectedFilterTemplateId) {
        // Don't proceed with API call and search if "Create New" is selected
        if (selectedFilterTemplateId === EDefaultFilterValues.CREATE_NEW) {
            this.setState({ selectedFilterTemplateId })
            return
        }

        const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === selectedFilterTemplateId)
        const userFilter = (selectedFilterTemplateId === EDefaultFilterValues.NONE) ? null : selectedFilterTemplate

		api.setSelectedTimecardUserFilter(AuthenticationStore.getLoggedInUser().Id, (selectedFilterTemplateId === EDefaultFilterValues.NONE) ? "" : selectedFilterTemplateId as string)
			.then(() => {
				this.setState({ 
					selectedFilterTemplateId,
					userFilter 
				})
			})
    }

    handleChangeFilterTemplates = (filterTemplates: any[]) => {
        this.setState({ filterTemplates }, () => {
            const userFilter = (this.state.selectedFilterTemplateId === EDefaultFilterValues.NONE) 
                ? null 
                : filterTemplates.find(i => i.Id === this.state.selectedFilterTemplateId);
                
            this.setState({ userFilter }, () => {
                // Force JobTimecards to reload by toggling a timestamp
                this.setState({ lastFilterUpdate: Date.now() });
            });
        });
    }

	async loadFilterTemplates() {
		try {
			const res = await api.getFilters(AuthenticationStore.getLoggedInUser().Id)
			const result = res.data as IUserFilterDocument
			const selectedFilterTemplateId = result && result.SelectedTimecardUserFilter ? result.SelectedTimecardUserFilter : EDefaultFilterValues.NONE
			const filterTemplates = result && result.TimecardUserFilters ? result.TimecardUserFilters : []
			const selectedFilterTemplate = filterTemplates.find(i => i.Id === selectedFilterTemplateId)
			const userFilter = (selectedFilterTemplateId === EDefaultFilterValues.NONE) ? null : selectedFilterTemplate
			
			// Set all filter-related state at once
			this.setState({ 
				filterTemplates, 
				selectedFilterTemplateId, 
				userFilter 
			})
		} catch (error) {
			console.error('Error loading filter templates:', error)
			this.setState({ 
				filterTemplates: [], 
				selectedFilterTemplateId: EDefaultFilterValues.NONE, 
				userFilter: null 
			})
		}
	}

    render() {
        return (
            <div className="detail-tab">
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    {/* Left side - Filter */}
                    <div>
                        <UserFilter
                            filterTemplates={this.state.filterTemplates}
                            selectedFilterTemplateId={this.state.selectedFilterTemplateId}
                            handleSelectFilterTemlate={this.handleSelectFilterTemplate}
                            handleChangeFilterTemplates={this.handleChangeFilterTemplates}
                            isTimecardFilter={true}
                        />
                    </div>

                    {/* Center - Header */}
                    <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "0 20px" }}>
                        <div style={{ fontSize: 25, fontWeight: "bold" }}>{strings.Timecards}</div>
                    </div>

                    {/* Right side - Toggle */}
                    <div>
                        {AccountStore.getPayClassEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) &&
                            <Toggle
                                data-intercom-target={"menu-AccountSettings-AccrualAdjustmentRules-Toggle-RequireAdjustmentNote"}
                                toggled={this.state.showConsolidatedTimecardView}
                                onToggle={(event, value) => {
                                    this.setState({ showConsolidatedTimecardView: value })
                                    localStorage.setItem(`findd_${this.showConsolidatedTimecardViewLocalStorageKey}`, value)
                                }}
                                labelPosition="left"
                                label={strings.Show_Consolidated_Timecard_View}
                                labelStyle={{ whiteSpace: "nowrap" }}
                            />
                        }
                    </div>
                </div>
                <div style={{ marginTop: 20, clear: "both" }}>
                    {this.state.showConsolidatedTimecardView ?
                        <JobDetailConsolidatedTimecards
                            jobId={this.props.job.Id}
                            referenceDate={this.state.referenceDate}
                            selectedPayrollPayCalendar={this.state.selectedPayrollPayCalendar}
                            selectedFilterTemplateId={this.state.selectedFilterTemplateId}
                            handleChangeReferenceDate={referenceDate => {
                                this.setState({ referenceDate })
                                localStorage.setItem(`findd_${this.referenceDateLocalStorageKey}`, moment(referenceDate).format())
                            }}
                            userFilter={this.state.userFilter}
                        /> :
                        <JobTimecards
                            job={this.props.job}
                            referenceDate={this.state.referenceDate}
                            selectedPayrollPayCalendar={this.state.selectedPayrollPayCalendar}
                            selectedFilterTemplateId={this.state.selectedFilterTemplateId}
                            handleChangeReferenceDate={referenceDate => {
                                this.setState({ referenceDate })
                                localStorage.setItem(`findd_${this.referenceDateLocalStorageKey}`, moment(referenceDate).format())
                            }}
                            handleChangePayrollPayCalendar={selectedPayrollPayCalendar => this.setState({ selectedPayrollPayCalendar })}
                            userFilter={this.state.userFilter}
                            lastFilterUpdate={this.state.lastFilterUpdate}
                        />
                    }
                </div>
            </div>
        )
    }
}
